import type { ZipRequest, ZipTree } from "../../caseFiles/api/filesystemApi";
import { subtreeIds, type TreeNodeLike } from "./treeRows";

/** What the footer and the toast count for a scope. */
export type ZipSummary = {
  files: number;
  /** Total size when every file's size is known. */
  bytes: number | null;
};

/** The archive's name, as the server names it. */
export const zipName = (
  tree: ZipTree,
  request: Pick<ZipRequest, "scope">,
  { count, folderName }: { count: number; folderName?: string | null }
): string => {
  const root = tree === "files" ? "Case Files" : "Work Products";
  if (request.scope === "folder" && folderName) return `${folderName}.zip`;
  if (request.scope === "selected")
    return `${root} (${count} ${count === 1 ? "Item" : "Items"}).zip`;
  return `${root}.zip`;
};

/** The non-folder nodes a request covers; selected folders bring their contents. */
export const nodesInScope = <N extends TreeNodeLike>(
  nodes: N[],
  request: Pick<ZipRequest, "scope" | "node_ids" | "folder_id">
): N[] => {
  const live = nodes.filter((n) => !n.is_deleted);
  const files = (keep: Set<string> | null) =>
    live.filter((n) => n.kind !== "folder" && (!keep || keep.has(n.id)));
  if (request.scope === "all") return files(null);
  const roots =
    request.scope === "folder"
      ? request.folder_id
        ? [request.folder_id]
        : []
      : request.node_ids ?? [];
  const keep = new Set<string>();
  roots.forEach((id) => subtreeIds(live, id).forEach((x) => keep.add(x)));
  return files(keep);
};

/** Files and bytes a request covers, from nodes that carry `size_bytes`. */
export const summarize = <
  N extends TreeNodeLike & { size_bytes?: number | null }
>(
  nodes: N[],
  request: Pick<ZipRequest, "scope" | "node_ids" | "folder_id">
): ZipSummary => {
  const covered = nodesInScope(nodes, request);
  const known = covered.every((n) => typeof n.size_bytes === "number");
  return {
    files: covered.length,
    bytes: known
      ? covered.reduce((sum, n) => sum + (n.size_bytes ?? 0), 0)
      : null
  };
};
